/**
 * A uniform grid over points in space, with a group filter.
 *
 * Two features ask the same question of the body's geometry — "what else is at
 * this point?" — and answer it differently only in who is allowed to reply:
 *
 *   - the mirror map reflects a texel and asks for the texel on the OPPOSITE
 *     part, because the left arm's mirror can only be on the right arm;
 *   - the seam map asks for the nearest texel in ANY OTHER region, because two
 *     faces that meet on the body meet at a point they both nearly occupy.
 *
 * Same index, opposite predicate. Extracting it is what
 * docs/decisions/mirror-and-editor-tools.md §4 means by "build it once, use it
 * for both".
 *
 * A k-d tree would be tidier and is not worth it: the points are texels on a
 * body, so they are near-uniformly dense over a few cubic studs, which is the
 * case a grid is best at. Cell size is the search radius, so a query touches 27
 * cells and no more.
 */
export class PointGrid {
  private readonly cells = new Map<number, number[]>();
  private readonly inv: number;

  constructor(
    private readonly xs: Float32Array,
    private readonly ys: Float32Array,
    private readonly zs: Float32Array,
    /**
     * Which set each point belongs to — a part index for the mirror map, a
     * region index for the seam map. Only ever compared, never interpreted.
     */
    private readonly group: Int32Array,
    count: number,
    cell: number,
  ) {
    this.inv = 1 / cell;
    for (let i = 0; i < count; i++) {
      const key = this.key(xs[i] as number, ys[i] as number, zs[i] as number);
      const bucket = this.cells.get(key);
      if (bucket) bucket.push(i);
      else this.cells.set(key, [i]);
    }
  }

  private key(x: number, y: number, z: number): number {
    // Three signed cell indices packed into one number. 1024 cells per axis is
    // far more than a 5-stud body needs at texel scale, and keeps the product
    // inside the exact-integer range.
    const cx = Math.floor(x * this.inv) + 512;
    const cy = Math.floor(y * this.inv) + 512;
    const cz = Math.floor(z * this.inv) + 512;
    return (cx * 1024 + cy) * 1024 + cz;
  }

  /**
   * Nearest point within `radius`, or -1.
   *
   * `want >= 0` accepts only that group; `want < 0` accepts any group EXCEPT
   * `exclude`. One walk, because the only difference between the two queries is
   * one comparison and duplicating the loop to avoid it would be worse.
   */
  private search(
    x: number,
    y: number,
    z: number,
    radius: number,
    want: number,
    exclude: number,
    accept?: (i: number) => boolean,
  ): number {
    let bestD = radius * radius;
    let best = -1;
    const cx = Math.floor(x * this.inv) + 512;
    const cy = Math.floor(y * this.inv) + 512;
    const cz = Math.floor(z * this.inv) + 512;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dz = -1; dz <= 1; dz++) {
          const bucket = this.cells.get(((cx + dx) * 1024 + (cy + dy)) * 1024 + (cz + dz));
          if (!bucket) continue;
          for (const i of bucket) {
            const g = this.group[i] as number;
            if (want >= 0 ? g !== want : g === exclude) continue;
            /*
              Rejected candidates must not end the search.

              The nearest point is frequently the WRONG one — a limb lies flush
              against the torso, so the closest thing to a torso edge texel is
              the arm face pressed against it, 0.0026 studs away, where the face
              it genuinely meets is 0.8727 away. Filtering after the fact would
              return nothing; filtering inside the walk lets the real neighbour
              win.
            */
            if (accept && !accept(i)) continue;
            const ex = (this.xs[i] as number) - x;
            const ey = (this.ys[i] as number) - y;
            const ez = (this.zs[i] as number) - z;
            const d = ex * ex + ey * ey + ez * ez;
            if (d < bestD) {
              bestD = d;
              best = i;
            }
          }
        }
      }
    }
    return best;
  }

  /**
   * Index of the nearest stored point within `radius` that belongs to
   * `wantGroup`, or -1.
   *
   * The group filter is not an optimisation, it is correctness. A limb sits
   * FLUSH against the torso, so the arm's inner face and the torso's side face
   * occupy the same plane — `rightLimb.left` and `torso.right` both live at
   * x = +1.000 on the classic rig. Reflecting a texel from either one lands on
   * a point where two parts coincide, and an unfiltered nearest-neighbour picks
   * whichever the iteration order reached first. Measured: `rightLimb.left`
   * paired at 7.1%, because the involution check then threw away almost every
   * pair it had made.
   */
  nearest(x: number, y: number, z: number, radius: number, wantGroup: number): number {
    return this.search(x, y, z, radius, wantGroup, -1);
  }

  /**
   * Index of the nearest stored point within `radius` in any group but
   * `excludeGroup`, or -1.
   *
   * This is the seam question. A texel on the outer ring of a face is a texel
   * at the edge of the body's flat side, and the surface continues over the
   * chamfer onto a face that may be nowhere near it in the template — the
   * torso's edge continues onto the arm, whose strip is both far away and
   * rotated. Excluding its own region is what makes the answer the neighbour
   * rather than the texel next door.
   */
  nearestOther(
    x: number,
    y: number,
    z: number,
    radius: number,
    excludeGroup: number,
    accept?: (i: number) => boolean,
  ): number {
    return this.search(x, y, z, radius, -1, excludeGroup, accept);
  }
}
